import { useState, useEffect } from 'react';
import { ConsultingFirmManager } from './ConsultingFirmManager';
import { TemplateManager } from './TemplateManager';
import { SubscriptionManagement } from './SubscriptionManagement';
import { DemoManagement } from './DemoManagement';
import AIPromptConfiguration from './AIPromptConfiguration';
import { GlobalQuestionManagement } from './GlobalQuestionManagement';
import { QuestionLibrary } from './QuestionLibrary';
import { EnvironmentBadge } from './EnvironmentBadge';
import { projectId } from '../utils/supabase/info';
import { getAuthHeaders, getAuthHeadersWithJson } from '../utils/apiHeaders';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Alert, AlertDescription } from './ui/alert';
import { Shield, Building2, Package, Crown, Database, Ticket, UserPlus, Edit, RefreshCw, Loader2, Filter, Users, Lock, ScrollText, Activity, Trash2, Archive, LifeBuoy, Globe, Info, Code, UserCog, Mail, Wrench } from 'lucide-react';
import { CompanySetup } from './CompanySetup';
import { ConsultantSetup } from './ConsultantSetup';
import { UserCreateDialog } from './UserCreateDialog';
import { CompanyEditDialog } from './CompanyEditDialog';
import { SystemAdminTenants } from './SystemAdminTenants';
import { SystemAdminOperations } from './SystemAdminOperations';
import { ConfigurationPage } from './ConfigurationPage';
import { PromoCodeManager } from './PromoCodeManager';
import { PermissionsManagement } from './PermissionsManagement';
import { AuditLog } from './AuditLog';
import { QueueItemsCleanup } from './QueueItemsCleanup';
import { SystemAdminProtectionCheck } from './SystemAdminProtectionCheck';
import { CompanyDataDiagnostic } from './CompanyDataDiagnostic';
import { DataRestoration } from './DataRestoration';
import { PerformanceDiagnostic } from './PerformanceDiagnostic';
import { KVDiagnostics } from './KVDiagnostics';
import { CompanyDataFix } from './CompanyDataFix';
import { TopNavigation } from './TopNavigation';
import { SystemAdminSupportQueue } from './SystemAdminSupportQueue';
import { DevTools } from './DevTools';
import { ControlGridSystemAdminTab } from './ControlGridSystemAdminTab';

import { useImpersonation } from './ImpersonationContext';

interface User {
  id: string;
  email: string;
  name?: string;
  companyId: string;
  companyName: string;
  team: string | null;
  role: string;
  isAdmin: boolean;
  isCompanyAdmin: boolean;
  status: string;
  consultantCompanies?: string[];
  createdAt: string;
  isDemo?: boolean;
  firmId?: string;
}

interface Company {
  id: string;
  name: string;
  userCount?: number;
  createdAt: string;
  sowStatus?: string | null;
  sowCount?: number;
  activeSOWCount?: number;
  activeChangeOrderCount?: number;
}

interface SystemAdminPanelProps {
  accessToken: string;
  currentUser?: any;
  onLogout?: () => void;
  /** Opens Edit Company for this company (the old `/company/:id` dashboard links land here). */
  initialCompanyId?: string | null;
}

/** The tab a link asks for (`/system-admin?tab=operations`, used by the retired diagnostic pages' addresses). */
export function tabFromLocation(search: string): string | null {
  const tab = new URLSearchParams(search).get('tab');
  return tab && /^[a-z-]+$/.test(tab) ? tab : null;
}

export function SystemAdminPanel({ accessToken, currentUser, onLogout, initialCompanyId }: SystemAdminPanelProps) {
  console.log('[LOGOUT_TRACE] SystemAdminPanel received onLogout prop:', typeof onLogout);
  const [activeTab, setActiveTab] = useState(() => (typeof window === 'undefined' ? null : tabFromLocation(window.location.search)) || 'users');
  
  // Check if we're on a demo tenant subdomain or staging (not production)
  const isDemoEnvironment = typeof window !== 'undefined' && (window.location.hostname.endsWith('-demo.pathkit.pro') || window.location.hostname === 'staging.pathkit.pro');
  
  // Check if we're in production (pathkit.pro without subdomains except demo)
  const isProduction = typeof window !== 'undefined' && 
    (window.location.hostname === 'pathkit.pro' || 
    window.location.hostname === 'www.pathkit.pro' ||
    window.location.hostname === 'app.pathkit.pro');
  
  // All users state
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [consultingFirms, setConsultingFirms] = useState<any[]>([]);
  
  // Track which tabs have been loaded
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [companiesLoaded, setCompaniesLoaded] = useState(false);
  const [lastUserLoad, setLastUserLoad] = useState<Date | null>(null);
  const [lastCompanyLoad, setLastCompanyLoad] = useState<Date | null>(null);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editRole, setEditRole] = useState('user');
  const [editCompanyId, setEditCompanyId] = useState('');
  const [editConsultantCompanies, setEditConsultantCompanies] = useState<string[]>([]);
  const [editFirmId, setEditFirmId] = useState('');
  const [editIsDemo, setEditIsDemo] = useState(false);
  const [editEmail, setEditEmail] = useState('');
  const [editName, setEditName] = useState('');
  const [editDialogSaving, setEditDialogSaving] = useState(false);
  const [editDialogError, setEditDialogError] = useState('');
  const [editDialogDeleting, setEditDialogDeleting] = useState(false);
  
  // Company edit dialog state
  const [companyEditDialogOpen, setCompanyEditDialogOpen] = useState(false);
  const [editingCompanyId, setEditingCompanyId] = useState<string | null>(null);
  const [editingCompanyProjects, setEditingCompanyProjects] = useState<any[]>([]);
  
  // User create dialog state
  const [userCreateDialogOpen, setUserCreateDialogOpen] = useState(false);
  
  // Company setup dialog state
  const [companySetupOpen, setCompanySetupOpen] = useState(false);
  
  // Consultant setup dialog state
  const [consultantSetupOpen, setConsultantSetupOpen] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showOnlyActiveSOW, setShowOnlyActiveSOW] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [showEmergencyLogout, setShowEmergencyLogout] = useState(false);
  
  // Resend onboarding
  const [resendingUserId, setResendingUserId] = useState<string | null>(null);
  const [resendSuccess, setResendSuccess] = useState<string | null>(null);
  
  // Impersonation
  const { startImpersonation } = useImpersonation();
  const [impersonatingId, setImpersonatingId] = useState<string | null>(null);

  // Load data lazily when tab is first accessed
  useEffect(() => {
    if (activeTab === 'users' && !usersLoaded) {
      loadAllUsers();
    } else if (activeTab === 'companies' && !companiesLoaded) {
      loadCompanies();
    }
  }, [activeTab]);

  // Load consulting firms on mount (needed for CF Admin role assignment)
  useEffect(() => {
    loadConsultingFirms();
  }, []);

  // Listen for demo users created event
  useEffect(() => {
    const handleDemoUsersCreated = () => {
      console.log('[FRONTEND] SystemAdminPanel: Demo users created, refreshing user list...');
      // If we're on the users tab, reload
      if (activeTab === 'users') {
        setUsersLoaded(false); // Force reload
        loadAllUsers();
      } else {
        // Mark users as needing reload when tab is next accessed
        setUsersLoaded(false);
      }
    };

    window.addEventListener('pathkit-demo-users-created', handleDemoUsersCreated);
    
    return () => {
      window.removeEventListener('pathkit-demo-users-created', handleDemoUsersCreated);
    };
  }, [activeTab]);

  const loadAllUsers = async () => {
    console.log('[FRONTEND] SystemAdmin: Starting to load all users...');
    console.log('[FRONTEND] SystemAdmin: Token format:', accessToken ? `${accessToken.substring(0, 10)}... (length=${accessToken.length})` : 'EMPTY/MISSING');
    const startTime = Date.now();
    setLoading(true);
    setError('');

    try {
      const fetchStart = Date.now();
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/all-users`,
        {
          headers: getAuthHeaders(accessToken),
        }
      );
      console.log(`[FRONTEND] SystemAdmin: Users fetch completed in ${Date.now() - fetchStart}ms, status: ${response.status}`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        if (response.status === 401) {
          console.error('[FRONTEND] SystemAdmin: 401 on /admin/all-users -- session likely expired or pruned from KV. Token:', accessToken ? `${accessToken.substring(0, 15)}...` : 'EMPTY');
        }
        throw new Error(errorData.error || `Failed to load users (${response.status})`);
      }

      const data = await response.json();
      setAllUsers(data.users);
      // Filter out Scopify from company list - it's internal only
      setCompanies(data.companies.filter((c: Company) => c.name !== 'Scopify'));
      setUsersLoaded(true);
      // Only mark companies loaded if we actually got some from this endpoint;
      // otherwise let the dedicated /admin/companies endpoint try when the tab is clicked
      if (data.companies && data.companies.length > 0) {
        setCompaniesLoaded(true);
      }
      console.log(`[FRONTEND] SystemAdmin: Loaded ${data.users?.length || 0} users, ${data.companies?.length || 0} companies in ${Date.now() - startTime}ms`);

    } catch (err: any) {
      console.error('[FRONTEND] SystemAdmin: Error loading users:', err);
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    console.log('[FRONTEND] SystemAdmin: Starting to load companies...');
    const startTime = Date.now();
    setLoading(true);
    setError('');

    try {
      const fetchStart = Date.now();
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/companies`,
        {
          headers: getAuthHeaders(accessToken),
        }
      );
      console.log(`[FRONTEND] SystemAdmin: Companies fetch completed in ${Date.now() - fetchStart}ms, status: ${response.status}`);

      if (!response.ok) {
        throw new Error('Failed to load companies');
      }

      const data = await response.json();
      // Filter out Scopify from company list - it's internal only
      setCompanies(data.companies.filter((c: Company) => c.name !== 'Scopify'));
      setCompaniesLoaded(true);
      console.log(`[FRONTEND] SystemAdmin: Loaded ${data.companies?.length || 0} companies in ${Date.now() - startTime}ms`);
      
      // DIAGNOSTIC: Check user counts
      const companiesWithUsers = data.companies.filter((c: Company) => (c.userCount || 0) > 0);
      console.log(`[FRONTEND] SystemAdmin: Companies with users: ${companiesWithUsers.length}`, 
        companiesWithUsers.map((c: Company) => ({ name: c.name, userCount: c.userCount })));

    } catch (err: any) {
      console.error('[FRONTEND] SystemAdmin: Error loading companies:', err);
      setError(err.message || 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const loadConsultingFirms = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/consulting-firms`,
        {
          headers: getAuthHeaders(accessToken),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setConsultingFirms(data.firms || []);
      }
    } catch (err) {
      console.error('Error loading consulting firms:', err);
    }
  };

  const openEditDialog = (user: User) => {
    setEditingUser(user);
    setEditRole(user.role || 'user');
    setEditCompanyId(user.companyId);
    setEditConsultantCompanies(user.consultantCompanies || []);
    setEditFirmId(user.firmId || '');
    setEditIsDemo(user.isDemo || false);
    setEditEmail(user.email || '');
    setEditName(user.name || '');
    setEditDialogError('');
    setEditDialogOpen(true);
    loadConsultingFirms(); // Load consulting firms for CF Admin role selector
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;

    if (!editEmail.trim()) {
      setEditDialogError('Email is required');
      return;
    }

    try {
      setEditDialogSaving(true);
      setEditDialogError('');

      // Update email/name if changed (via /admin/update-user)
      const emailChanged = editEmail.trim() !== editingUser.email;
      const nameChanged = editName.trim() !== (editingUser.name || '');
      if (emailChanged || nameChanged) {
        const userResponse = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/update-user`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeaders(accessToken),
            },
            body: JSON.stringify({
              userId: editingUser.id,
              email: editEmail.trim(),
              name: editName.trim() || null,
            }),
          }
        );

        if (!userResponse.ok) {
          const data = await userResponse.json().catch(() => ({ error: 'Unknown error' }));
          throw new Error(data.error || 'Failed to update user email/name');
        }
      }

      // Update role
      const roleResponse = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/update-user-role`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(accessToken),
          },
          body: JSON.stringify({
            userId: editingUser.id,
            role: editRole,
            consultantCompanies: editRole === 'consultant' ? editConsultantCompanies : [],
            firmId: (editRole === 'cfAdmin' || editRole === 'billingAdmin' || editRole === 'bdRep') ? editFirmId : undefined,
            isDemo: editIsDemo,
          }),
        }
      );

      if (!roleResponse.ok) {
        const data = await roleResponse.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(data.error || 'Failed to update user role');
      }

      // Update company if changed (only for non-firm roles)
      if (editRole !== 'cfAdmin' && editRole !== 'billingAdmin' && editRole !== 'bdRep' && editRole !== 'systemAdmin' && editCompanyId !== editingUser.companyId) {
        const companyResponse = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/assign-company`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeaders(accessToken),
            },
            body: JSON.stringify({
              userId: editingUser.id,
              companyId: editCompanyId,
            }),
          }
        );

        if (!companyResponse.ok) {
          const data = await companyResponse.json().catch(() => ({ error: 'Unknown error' }));
          throw new Error(data.error || 'Failed to assign company');
        }
      }

      setEditDialogOpen(false);
      setEditingUser(null);
      await loadAllUsers();

    } catch (err: any) {
      console.error('Error updating user:', err);
      setEditDialogError(err.message || 'Failed to update user');
    } finally {
      setEditDialogSaving(false);
    }
  };

  const toggleConsultantCompany = (companyId: string) => {
    if (editConsultantCompanies.includes(companyId)) {
      setEditConsultantCompanies(editConsultantCompanies.filter(id => id !== companyId));
    } else {
      setEditConsultantCompanies([...editConsultantCompanies, companyId]);
    }
  };

  const handleDeleteUser = async (user: User) => {
    // Prevent deleting yourself
    if (user.id === currentUser?.id) {
      alert('You cannot delete your own account while logged in.');
      return;
    }

    // Confirm deletion
    const confirmed = confirm(
      `PERMANENTLY DELETE user ${user.email}?\n\nThis action CANNOT be undone and will:\n- Remove the user from Supabase Auth\n- Delete their KV store data\n- Remove all their access permissions\n\nAre you sure you want to continue?`
    );

    if (!confirmed) return;

    setDeletingUserId(user.id);
    setError('');
    setEditDialogDeleting(true);
    setEditDialogError('');

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/delete-user/${user.id}`,
        {
          method: 'DELETE',
          headers: getAuthHeaders(accessToken),
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(data.error || 'Failed to delete user');
      }

      // Close the edit dialog if open
      setEditDialogOpen(false);
      setEditingUser(null);

      // Reload the user list
      await loadAllUsers();

    } catch (err: any) {
      console.error('Error deleting user:', err);
      setError(err.message || 'Failed to delete user');
      setEditDialogError(err.message || 'Failed to delete user');
    } finally {
      setDeletingUserId(null);
      setEditDialogDeleting(false);
    }
  };

  const handleArchiveUser = async (user: User) => {
    if (user.id === currentUser?.id) {
      alert('You cannot archive your own account while logged in.');
      return;
    }

    const confirmed = confirm(
      `Archive user ${user.email}?\n\nThis will:\n- Mark the user as archived\n- Prevent them from logging in\n- Preserve their data for audit purposes\n\nYou can restore archived users later.`
    );

    if (!confirmed) return;

    setEditDialogDeleting(true);
    setEditDialogError('');

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/delete-user/${user.id}?soft=true`,
        {
          method: 'DELETE',
          headers: getAuthHeaders(accessToken),
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(data.error || 'Failed to archive user');
      }

      setEditDialogOpen(false);
      setEditingUser(null);
      await loadAllUsers();

    } catch (err: any) {
      console.error('Error archiving user:', err);
      setEditDialogError(err.message || 'Failed to archive user');
    } finally {
      setEditDialogDeleting(false);
    }
  };

  const handleRestoreUser = async (user: User) => {
    const confirmed = confirm(
      `Restore user ${user.email}?\n\nThis will:\n- Set their status back to Active\n- Allow them to log in again`
    );

    if (!confirmed) return;

    setError('');

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/restore-user/${user.id}`,
        {
          method: 'POST',
          headers: getAuthHeaders(accessToken),
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(data.error || 'Failed to restore user');
      }

      await loadAllUsers();

    } catch (err: any) {
      console.error('Error restoring user:', err);
      setError(err.message || 'Failed to restore user');
    }
  };

  const handleImpersonateUser = async (user: User) => {
    // Prevent impersonating yourself or other admins (though backend blocks it too)
    if (user.id === currentUser?.id) return;
    
    // Additional confirmation
    if (!confirm(`View application as ${user.email}? This session will be audited.`)) return;
    
    setImpersonatingId(user.id);
    try {
        await startImpersonation(accessToken, user.id, "System Admin Impersonation via User Table");
        // AppRouter will handle the switch
    } catch (err: any) {
        console.error('Impersonation failed:', err);
    } finally {
        setImpersonatingId(null);
    }
  };

  const handleResendOnboarding = async (user: User) => {
    setResendingUserId(user.id);
    setResendSuccess(null);
    setError('');

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/resend-onboarding`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(accessToken),
          },
          body: JSON.stringify({ email: user.email }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to resend onboarding email');
      }

      if (data.emailSent) {
        setResendSuccess(user.email);
        // Auto-clear success message after 5s
        setTimeout(() => setResendSuccess(null), 5000);
      } else {
        throw new Error(data.error || 'Email could not be sent');
      }
    } catch (err: any) {
      console.error('Error resending onboarding email:', err);
      setError(`Failed to resend onboarding email to ${user.email}: ${err.message}`);
    } finally {
      setResendingUserId(null);
    }
  };

  const getRoleBadge = (user: User) => {
    if (user.role === 'systemAdmin' || user.isAdmin) {
      return <Badge variant="default">System Admin</Badge>;
    }
    if (user.role === 'cfAdmin') {
      return <Badge variant="secondary" className="bg-blue-600 text-white">Firm Admin</Badge>;
    }
    if (user.role === 'billingAdmin') {
      return <Badge variant="secondary" className="bg-purple-600 text-white">Billing Admin</Badge>;
    }
    if (user.role === 'bdRep') {
      return <Badge variant="secondary" className="bg-green-600 text-white">Business Development Rep</Badge>;
    }
    if (user.role === 'companyAdmin' || user.isCompanyAdmin) {
      return <Badge variant="secondary">Client Engagement Lead</Badge>;
    }
    if (user.role === 'consultant') {
      return <Badge variant="outline" className="border-blue-500 text-blue-700">Consultant</Badge>;
    }
    return <Badge variant="outline">User</Badge>;
  };

  // The old /company/:id dashboard links open Edit Company here
  useEffect(() => {
    if (initialCompanyId) void openCompanyEditDialog(initialCompanyId);
  }, [initialCompanyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCompanyEditDialog = async (companyId: string) => {
    setEditingCompanyId(companyId);
    setCompanyEditDialogOpen(true);
    
    // Load company data to get projects
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}`,
        {
          headers: getAuthHeaders(accessToken),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setEditingCompanyProjects(data.projects || []);
      } else {
        setEditingCompanyProjects([]);
      }
    } catch (error) {
      console.error('Error loading company data:', error);
      setEditingCompanyProjects([]);
    }
  };

  const getSOWStatusBadge = (company: Company) => {
    if (!company.sowStatus) {
      return <Badge variant="outline" className="text-muted-foreground">No SOW</Badge>;
    }
    
    switch (company.sowStatus) {
      case 'active':
        return (
          <div className="flex items-center gap-1">
            <Badge variant="default" className="bg-green-600">Active</Badge>
            {company.activeChangeOrderCount! > 0 && (
              <Badge variant="secondary">{company.activeChangeOrderCount} CO</Badge>
            )}
          </div>
        );
      case 'completed':
        return <Badge variant="secondary" className="bg-blue-600">Completed</Badge>;
      case 'draft':
        return <Badge variant="outline">Draft</Badge>;
      case 'cancelled':
        return <Badge variant="destructive">Canceled</Badge>;
      default:
        return <Badge variant="outline">{company.sowStatus}</Badge>;
    }
  };

  const filteredCompanies = showOnlyActiveSOW 
    ? companies.filter(c => c.sowStatus === 'active' || (c.activeChangeOrderCount && c.activeChangeOrderCount > 0))
    : companies;

  return (
    <div className="min-h-screen bg-muted/30">
      <TopNavigation
        user={{
          id: currentUser?.id || '',
          name: currentUser?.name || currentUser?.email?.split('@')[0] || 'System Admin',
          email: currentUser?.email || '',
          role: currentUser?.role || 'systemAdmin',
          avatarUrl: currentUser?.avatarUrl,
          isDemo: currentUser?.isDemo || false,
          roles: currentUser?.roles || [currentUser?.role || 'systemAdmin'],
          activeRole: currentUser?.activeRole || currentUser?.role || 'systemAdmin',
        }}
        companyId=""
        companyName="System Administration"
        accessToken={accessToken}
        onLogout={onLogout || (() => {
          console.error('[LOGOUT_TRACE] ❌ SystemAdminPanel - onLogout not provided, using fallback');
        })}
      />
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="h-6 w-6" />
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-semibold">System Administration</h1>
                <EnvironmentBadge />
              </div>
              <p className="text-sm text-muted-foreground">
                Manage all users, companies, and access across the system
              </p>
            </div>
          </div>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {resendSuccess && (
          <Alert className="border-green-300 bg-green-50 dark:bg-green-950">
            <Mail className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800 dark:text-green-200">
              Onboarding email resent successfully to <strong>{resendSuccess}</strong>
            </AlertDescription>
          </Alert>
        )}

        {/* Emergency Session Clear - Show when getting 403 errors */}
        {error.includes('Forbidden') && (
          <Card className="border-yellow-500 bg-yellow-50 dark:bg-yellow-950">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200">
                <Shield className="h-5 w-5" />
                Session Issue Detected
              </CardTitle>
              <CardDescription className="text-yellow-700 dark:text-yellow-300">
                Your session token may be invalid or expired. This can happen if your user account was recently modified or deleted.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-white dark:bg-gray-900 p-3 rounded border">
                <p className="text-sm font-mono text-gray-700 dark:text-gray-300">
                  Current User ID: {currentUser?.id || 'Unknown'}<br />
                  Email: {currentUser?.email || 'Unknown'}<br />
                  Role: {currentUser?.role || 'Unknown'}<br />
                  isAdmin: {String(currentUser?.isAdmin) || 'Unknown'}
                </p>
              </div>
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                If isAdmin is "false" or "undefined", your admin permissions need to be fixed. Click "Fix Admin Permissions" below.
              </p>
              <div className="flex gap-2">
                <Button
                  variant="default"
                  onClick={async () => {
                    try {
                      const response = await fetch(
                        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/fix-my-admin-permissions`,
                        {
                          method: 'POST',
                          headers: getAuthHeaders(accessToken),
                        }
                      );

                      if (!response.ok) {
                        const data = await response.json().catch(() => ({ error: 'Unknown error' }));
                        throw new Error(data.error || 'Failed to fix permissions');
                      }

                      alert('Admin permissions fixed! Please refresh the page.');
                      window.location.reload();
                    } catch (err: any) {
                      console.error('Error fixing admin permissions:', err);
                      alert(`Failed to fix permissions: ${err.message}`);
                    }
                  }}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Fix Admin Permissions
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    // Clear all session data
                    localStorage.clear();
                    sessionStorage.clear();
                    
                    // Clear cookies by setting them to expire
                    document.cookie.split(";").forEach(function(c) { 
                      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
                    });
                    
                    console.log('[Emergency Logout] Session cleared, redirecting to login...');
                    
                    // Redirect to login
                    window.location.href = '/login';
                  }}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white"
                >
                  Clear Session & Re-Login
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full flex flex-wrap gap-1 h-auto p-1">
            <TabsTrigger value="users" className="flex-shrink-0">
              <Users className="h-4 w-4 mr-2 hidden sm:inline" />
              All Users
            </TabsTrigger>
            <TabsTrigger value="companies" className="flex-shrink-0">
              <Building2 className="h-4 w-4 mr-2 hidden sm:inline" />
              Companies
            </TabsTrigger>
            <TabsTrigger value="consulting-firms" className="flex-shrink-0">
              <Building2 className="h-4 w-4 mr-2 hidden sm:inline" />
              <span className="hidden sm:inline">Consulting Firms</span>
              <span className="sm:hidden">Firms</span>
            </TabsTrigger>
            <TabsTrigger value="templates" className="flex-shrink-0">
              <Package className="h-4 w-4 mr-2 hidden sm:inline" />
              Templates
            </TabsTrigger>
            <TabsTrigger value="subscriptions" className="flex-shrink-0">
              <Crown className="h-4 w-4 mr-2 hidden sm:inline" />
              <span className="hidden sm:inline">Subscriptions</span>
              <span className="sm:hidden">Subs</span>
            </TabsTrigger>
            {isDemoEnvironment && (
              <TabsTrigger value="demo" className="flex-shrink-0">
                <Database className="h-4 w-4 mr-2 hidden sm:inline" />
                Demo
              </TabsTrigger>
            )}
            <TabsTrigger value="ai-config" className="flex-shrink-0">
              <Activity className="h-4 w-4 mr-2 hidden sm:inline" />
              <span className="hidden sm:inline">AI Configuration</span>
              <span className="sm:hidden">AI Config</span>
            </TabsTrigger>
            <TabsTrigger value="global-questions" className="flex-shrink-0">
              <Globe className="h-4 w-4 mr-2 hidden sm:inline" />
              <span className="hidden sm:inline">Global Questions</span>
              <span className="sm:hidden">Questions</span>
            </TabsTrigger>
            <TabsTrigger value="question-library" className="flex-shrink-0">
              <Info className="h-4 w-4 mr-2 hidden sm:inline" />
              <span className="hidden sm:inline">Question Library</span>
              <span className="sm:hidden">Library</span>
            </TabsTrigger>
            {/* DISABLED: Calendar integration temporarily disabled
            <TabsTrigger value="calendar">
              <Calendar className="h-4 w-4 mr-2" />
              Calendar
            </TabsTrigger>
            */}
            <TabsTrigger value="promo-codes" className="flex-shrink-0">
              <Ticket className="h-4 w-4 mr-2 hidden sm:inline" />
              <span className="hidden sm:inline">Promo Codes</span>
              <span className="sm:hidden">Promos</span>
            </TabsTrigger>
            <TabsTrigger value="support-queue" className="flex-shrink-0">
              <LifeBuoy className="h-4 w-4 mr-2 hidden sm:inline" />
              <span className="hidden sm:inline">Support Queue</span>
              <span className="sm:hidden">Support</span>
            </TabsTrigger>
            <TabsTrigger value="permissions" className="flex-shrink-0">
              <Lock className="h-4 w-4 mr-2 hidden sm:inline" />
              Permissions
            </TabsTrigger>
            <TabsTrigger value="audit" className="flex-shrink-0">
              <ScrollText className="h-4 w-4 mr-2 hidden sm:inline" />
              Audit Log
            </TabsTrigger>
            <TabsTrigger value="tenants" className="flex-shrink-0">
              <Globe className="h-4 w-4 mr-2 hidden sm:inline" />
              Tenants
            </TabsTrigger>
            <TabsTrigger value="operations" className="flex-shrink-0">
              <Wrench className="h-4 w-4 mr-2 hidden sm:inline" />
              Operations
            </TabsTrigger>
            <TabsTrigger value="configuration" className="flex-shrink-0">
              <Package className="h-4 w-4 mr-2 hidden sm:inline" />
              Configuration
            </TabsTrigger>
            <TabsTrigger value="diagnostics" className="flex-shrink-0">
              <Activity className="h-4 w-4 mr-2 hidden sm:inline" />
              Performance
            </TabsTrigger>
            <TabsTrigger value="control-grid" className="flex-shrink-0">
              <Activity className="h-4 w-4 mr-2 hidden sm:inline" />
              <span className="hidden sm:inline">Control Grid</span>
              <span className="sm:hidden">Grid</span>
            </TabsTrigger>
            {isDemoEnvironment && (
              <TabsTrigger value="dev" className="flex-shrink-0">
                <Code className="h-4 w-4 mr-2 hidden sm:inline" />
                <span className="hidden sm:inline">Dev Tools</span>
                <span className="sm:hidden">Dev</span>
              </TabsTrigger>
            )}
          </TabsList>

          {/* All Users Tab */}
          <TabsContent value="users">
            {loading && !usersLoaded ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">Loading users...</p>
                  <p className="text-sm text-muted-foreground mt-2">First load may take a few seconds</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>All Users ({allUsers.length})</CardTitle>
                      <CardDescription>
                        Manage user roles, companies, and access across the system
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          setUsersLoaded(false);
                          loadAllUsers();
                        }}
                        disabled={loading}
                      >
                        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                      </Button>
                      <Button variant="outline" onClick={() => setConsultantSetupOpen(true)}>
                        <UserPlus className="mr-2 h-4 w-4" />
                        Create Consultant
                      </Button>
                      <Button onClick={() => setUserCreateDialogOpen(true)}>
                        <UserPlus className="mr-2 h-4 w-4" />
                        Add User
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead>Team</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allUsers.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>{user.companyName}</TableCell>
                          <TableCell>{user.team || '—'}</TableCell>
                          <TableCell>{getRoleBadge(user)}</TableCell>
                          <TableCell>
                            <Badge variant={user.status === 'verified' || user.status === 'active' ? 'default' : 'secondary'}>
                              {user.status === 'verified' ? 'Verified' : user.status === 'active' ? 'Active' : user.status === 'pending' ? 'Pending' : user.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditDialog(user)}
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                            {user.id !== currentUser?.id && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="ml-2"
                                  onClick={() => handleImpersonateUser(user)}
                                  disabled={!!impersonatingId}
                                >
                                  {impersonatingId === user.id ? (
                                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                  ) : (
                                    <UserCog className="h-4 w-4 mr-1" />
                                  )}
                                  View As
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="ml-2"
                                  onClick={() => handleResendOnboarding(user)}
                                  disabled={resendingUserId === user.id}
                                  title="Resend onboarding email with password setup link"
                                >
                                  {resendingUserId === user.id ? (
                                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                  ) : (
                                    <Mail className="h-4 w-4 mr-1" />
                                  )}
                                  Resend Invite
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDeleteUser(user)}
                                  disabled={deletingUserId === user.id}
                                  className="ml-2"
                                >
                                  {deletingUserId === user.id ? (
                                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-4 w-4 mr-1" />
                                  )}
                                  Delete
                                </Button>
                                {(user.status === 'archived' || user.status === 'disabled') && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="ml-2 border-green-500 text-green-700 hover:bg-green-50"
                                    onClick={() => handleRestoreUser(user)}
                                  >
                                    <Archive className="h-4 w-4 mr-1" />
                                    Restore
                                  </Button>
                                )}
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Companies Tab */}
          <TabsContent value="companies">
            {loading && !companiesLoaded ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">Loading companies...</p>
                  <p className="text-sm text-muted-foreground mt-2">Calculating SOW status and user counts</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>All Companies ({filteredCompanies.length}{showOnlyActiveSOW && ` of ${companies.length}`})</CardTitle>
                      <CardDescription>
                        Overview of all companies in the system
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          setCompaniesLoaded(false);
                          loadCompanies();
                        }}
                        disabled={loading}
                      >
                        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                      </Button>
                      <Button
                        variant={showOnlyActiveSOW ? "default" : "outline"}
                        size="sm"
                        onClick={() => setShowOnlyActiveSOW(!showOnlyActiveSOW)}
                      >
                        <Filter className="mr-2 h-4 w-4" />
                        {showOnlyActiveSOW ? 'Show All' : 'Active SOWs Only'}
                      </Button>
                      <Button onClick={() => setCompanySetupOpen(true)}>
                        <Building2 className="mr-2 h-4 w-4" />
                        Create Company
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Company Name</TableHead>
                        <TableHead>SOW Status</TableHead>
                        <TableHead>Users</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredCompanies.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                            {showOnlyActiveSOW 
                              ? 'No companies with active SOWs found'
                              : 'No companies found'
                            }
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredCompanies.map((company) => (
                          <TableRow key={company.id}>
                            <TableCell>
                              <button
                                className="flex items-center gap-2 hover:underline text-left"
                                onClick={() => openCompanyEditDialog(company.id)}
                                disabled={!company.name}
                              >
                                <Building2 className="h-4 w-4 text-muted-foreground" />
                                {company.name || <span className="text-muted-foreground italic">(Unnamed - {company.id?.substring(0, 12)}...)</span>}
                              </button>
                            </TableCell>
                            <TableCell>
                              {getSOWStatusBadge(company)}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{company.userCount || 0} users</Badge>
                            </TableCell>
                            <TableCell>
                              {company.createdAt ? new Date(company.createdAt).toLocaleDateString() : '—'}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openCompanyEditDialog(company.id)}
                                disabled={!company.name}
                                title={!company.name ? 'Cannot edit unnamed company' : ''}
                              >
                                <Edit className="h-4 w-4 mr-1" />
                                Edit
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Consulting Firms Tab */}
          <TabsContent value="consulting-firms">
            <ConsultingFirmManager accessToken={accessToken} isSystemAdmin={true} />
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates">
            <TemplateManager accessToken={accessToken} currentUser={currentUser} />
          </TabsContent>

          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions">
            <SubscriptionManagement accessToken={accessToken} />
          </TabsContent>

          {/* Demo Management Tab (Staging/Demo only) */}
          {isDemoEnvironment && (
            <TabsContent value="demo">
              <DemoManagement accessToken={accessToken} />
            </TabsContent>
          )}

          {/* AI Configuration Tab */}
          <TabsContent value="ai-config">
            <AIPromptConfiguration accessToken={accessToken} />
          </TabsContent>

          {/* Global Questions Tab */}
          <TabsContent value="global-questions">
            <GlobalQuestionManagement accessToken={accessToken} currentUser={currentUser} />
          </TabsContent>

          {/* Question Library Tab */}
          <TabsContent value="question-library">
            <QuestionLibrary accessToken={accessToken} />
          </TabsContent>

          {/* DISABLED: Calendar integration temporarily disabled
          <TabsContent value="calendar">
            <CalendarManager accessToken={accessToken} />
          </TabsContent>
          */}

          {/* Promo Codes Tab */}
          <TabsContent value="promo-codes">
            <PromoCodeManager userEmail={currentUser?.email || ''} accessToken={accessToken} />
          </TabsContent>

          {/* Support Queue Tab */}
          <TabsContent value="support-queue">
            <SystemAdminSupportQueue accessToken={accessToken} />
          </TabsContent>

          {/* Permissions Tab */}
          <TabsContent value="permissions">
            <PermissionsManagement accessToken={accessToken} />
          </TabsContent>

          {/* Audit Log Tab */}
          <TabsContent value="audit">
            <AuditLog accessToken={accessToken} isSystemAdmin={true} />
          </TabsContent>

          {/* Performance Diagnostics Tab */}
          <TabsContent value="diagnostics">
            <div className="space-y-4">
              <QueueItemsCleanup accessToken={accessToken} />
              <CompanyDataFix accessToken={accessToken} />
              <SystemAdminProtectionCheck accessToken={accessToken} />
              <CompanyDataDiagnostic accessToken={accessToken} />
              <DataRestoration accessToken={accessToken} />
              <PerformanceDiagnostic accessToken={accessToken} />
              <KVDiagnostics accessToken={accessToken} />
            </div>
          </TabsContent>

          {/* Tenants, Operations and Configuration: the retired SystemAdminDashboard, the root-host diagnostic pages
              and ConfigurationPage (RESTORE_PLAN R3.6) */}
          <TabsContent value="tenants">
            <SystemAdminTenants accessToken={accessToken} onOpenCompany={openCompanyEditDialog} />
          </TabsContent>
          <TabsContent value="operations">
            <SystemAdminOperations accessToken={accessToken} />
          </TabsContent>
          <TabsContent value="configuration">
            <ConfigurationPage accessToken={accessToken} currentUser={currentUser} />
          </TabsContent>

          {/* Control Grid Tab — read-only product health rollup */}
          <TabsContent value="control-grid">
            <ControlGridSystemAdminTab />
          </TabsContent>

          {/* Dev Tools Tab */}
          {isDemoEnvironment && (
            <TabsContent value="dev">
              <DevTools accessToken={accessToken} />
            </TabsContent>
          )}
        </Tabs>

        {/* Company Setup Dialog */}
        <Dialog open={companySetupOpen} onOpenChange={setCompanySetupOpen}>
          <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Company</DialogTitle>
              <DialogDescription>
                Set up a new company with its first administrator
              </DialogDescription>
            </DialogHeader>
            <CompanySetup 
              accessToken={accessToken} 
              currentUser={currentUser}
              onCompanyCreated={() => {
                setCompanySetupOpen(false);
                loadCompanies();
                loadAllUsers();
              }}
            />
          </DialogContent>
        </Dialog>

        {/* Consultant Setup Dialog */}
        <Dialog open={consultantSetupOpen} onOpenChange={setConsultantSetupOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Consultant</DialogTitle>
              <DialogDescription>
                Create a consultant user with access to multiple companies
              </DialogDescription>
            </DialogHeader>
            <ConsultantSetup 
              accessToken={accessToken} 
              onConsultantCreated={() => {
                setConsultantSetupOpen(false);
                loadAllUsers();
              }}
            />
          </DialogContent>
        </Dialog>

        {/* User Create Dialog */}
        <UserCreateDialog
          open={userCreateDialogOpen}
          onClose={() => setUserCreateDialogOpen(false)}
          onUserCreated={() => {
            setUserCreateDialogOpen(false);
            loadAllUsers();
            loadCompanies();
          }}
          accessToken={accessToken}
          companies={companies}
          userRole="systemAdmin"
        />

        {/* Company Edit Dialog */}
        {editingCompanyId && (
          <CompanyEditDialog
            open={companyEditDialogOpen}
            onClose={() => {
              setCompanyEditDialogOpen(false);
              setEditingCompanyId(null);
              setEditingCompanyProjects([]);
            }}
            companyId={editingCompanyId}
            accessToken={accessToken}
            onUpdated={() => {
              loadCompanies();
            }}
            isSystemAdmin={true}
            projects={editingCompanyProjects}
            currentUser={currentUser}
          />
        )}

        {/* Edit User Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>
                Update user role, company assignment, and permissions for {editingUser?.email}
              </DialogDescription>
            </DialogHeader>

            {editDialogError && (
              <Alert variant="destructive">
                <AlertDescription>{editDialogError}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email Address</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  placeholder="user@example.com"
                />
                {editEmail.trim() !== (editingUser?.email || '') && (
                  <p className="text-sm text-amber-600">Email will be updated in Supabase Auth and KV store</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-name">Name</Label>
                <Input
                  id="edit-name"
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Full name"
                />
              </div>

              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={editRole} onValueChange={setEditRole}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Client Team Member</SelectItem>
                    <SelectItem value="consultant">Consultant</SelectItem>
                    <SelectItem value="companyAdmin">Client Engagement Lead</SelectItem>
                    <SelectItem value="cfAdmin">Firm Admin</SelectItem>
                    <SelectItem value="billingAdmin">Billing Admin</SelectItem>
                    <SelectItem value="bdRep">Business Development Rep</SelectItem>
                    <SelectItem value="systemAdmin">System Admin</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-muted-foreground">
                  {editRole === 'systemAdmin' && 'Full access to all companies and system management'}
                  {editRole === 'companyAdmin' && 'Can manage users within their company'}
                  {editRole === 'cfAdmin' && 'Belongs to the consulting firm and manages all client companies'}
                  {editRole === 'billingAdmin' && 'Belongs to the consulting firm and manages subscriptions and billing'}
                  {editRole === 'bdRep' && 'Belongs to the consulting firm and manages business development'}
                  {editRole === 'consultant' && 'Can access multiple assigned companies'}
                  {editRole === 'user' && 'Standard user access to their company'}
                </p>
              </div>

              {(editRole === 'cfAdmin' || editRole === 'billingAdmin' || editRole === 'bdRep') && (
                <div className="space-y-2">
                  <Label>Consulting Firm *</Label>
                  <Select value={editFirmId} onValueChange={setEditFirmId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select consulting firm" />
                    </SelectTrigger>
                    <SelectContent>
                      {consultingFirms.map((firm) => (
                        <SelectItem key={firm.id} value={firm.id}>
                          {firm.name} ({firm.subdomain})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-muted-foreground">
                    {editRole === 'cfAdmin' && 'CF Admins belong to the firm and can manage all client companies'}
                    {editRole === 'billingAdmin' && 'Billing Admins belong to the firm and manage subscriptions'}
                    {editRole === 'bdRep' && 'BD Reps belong to the firm and manage business development'}
                  </p>
                </div>
              )}

              {/* Only show Company for non-firm roles (users, consultants, company admins) */}
              {editRole !== 'systemAdmin' && editRole !== 'cfAdmin' && editRole !== 'billingAdmin' && editRole !== 'bdRep' && (
                <div className="space-y-2">
                  <Label>Company</Label>
                  <Select value={editCompanyId} onValueChange={setEditCompanyId}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map((company) => (
                        <SelectItem key={company.id} value={company.id}>
                          {company.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* System Admin company is fixed to Scopify */}
              {editRole === 'systemAdmin' && (
                <div className="space-y-2">
                  <Label>Company</Label>
                  <div className="flex items-center h-10 px-3 py-2 border rounded-md bg-muted text-muted-foreground">
                    Scopify (Fixed - System Admins belong to Scopify)
                  </div>
                </div>
              )}

              {editRole === 'consultant' && (
                <div className="space-y-2">
                  <Label>Consultant Company Access</Label>
                  <p className="text-muted-foreground">
                    Select which companies this consultant can access
                  </p>
                  <div className="border rounded-lg p-4 space-y-2 max-h-60 overflow-y-auto">
                    {companies.map((company) => (
                      <div key={company.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`consultant-company-${company.id}`}
                          checked={editConsultantCompanies.includes(company.id)}
                          onCheckedChange={() => toggleConsultantCompany(company.id)}
                        />
                        <Label 
                          htmlFor={`consultant-company-${company.id}`}
                          className="cursor-pointer flex-1"
                        >
                          {company.name}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Demo User Checkbox */}
              <div className="space-y-2 border-t pt-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="demo-user-checkbox"
                    checked={editIsDemo}
                    onCheckedChange={(checked) => setEditIsDemo(!!checked)}
                  />
                  <Label htmlFor="demo-user-checkbox" className="cursor-pointer">
                    Demo User
                  </Label>
                </div>
                <p className="text-sm text-muted-foreground">
                  Demo users can switch between roles without logging out, useful for product demonstrations
                </p>
              </div>
            </div>

            <DialogFooter>
              <div className="flex justify-between items-center w-full">
                <div>
                  {editingUser && editingUser.id !== currentUser?.id && (
                    <div className="flex gap-2">
                      {(editingUser.status === 'archived' || editingUser.status === 'disabled') && (
                        <Button
                          variant="outline"
                          className="border-green-500 text-green-700 hover:bg-green-50"
                          onClick={() => {
                            handleRestoreUser(editingUser);
                            setEditDialogOpen(false);
                          }}
                        >
                          <Archive className="h-4 w-4 mr-2" />
                          Restore User
                        </Button>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="destructive"
                            disabled={editDialogDeleting}
                          >
                            {editDialogDeleting ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4 mr-2" />
                            )}
                            {editDialogDeleting ? 'Processing...' : 'Remove User'}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          <DropdownMenuItem onClick={() => editingUser && handleArchiveUser(editingUser)} disabled={editDialogDeleting}>
                            <Archive className="h-4 w-4 mr-2" />
                            <div className="flex flex-col">
                              <span>Archive User</span>
                              <span className="text-xs text-muted-foreground">Preserves data, can be restored</span>
                            </div>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => editingUser && handleDeleteUser(editingUser)} disabled={editDialogDeleting} className="text-destructive">
                          <Trash2 className="h-4 w-4 mr-2" />
                          <div className="flex flex-col">
                            <span>Permanently Delete</span>
                            <span className="text-xs text-muted-foreground">Cannot be undone</span>
                          </div>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleUpdateUser} disabled={editDialogSaving}>
                    {editDialogSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </Button>
                </div>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}